import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, Download, FileDown, Trash2 } from 'lucide-react';
import {
  StandardLoadingState,
  StandardErrorState
} from '../standard';
import {
  fetchRetentionSettings,
  updateRetentionPolicy,
  updateSLASettings,
  fetchAdvancedRetentionSettings,
  updateAutomationSettings,
  updateNotificationSettings,
  updateComplianceSettings,
  fetchOverviewStats,
  fetchOverviewUsers,
  RetentionPolicy,
  SLASetting,
  AutomationSettings,
  NotificationSettings,
  ComplianceSettings
} from '../../services/api/dataRetentionApi';

type RetentionTab = 'overview' | 'policies' | 'sla' | 'automation' | 'notifications' | 'compliance';

interface Tab {
  id: RetentionTab;
  label: string;
  icon: string;
}

// ============================================
// OVERVIEW TAB
// ============================================

interface OverviewTabProps {
  policies: RetentionPolicy[];
}

const OverviewTab: React.FC<OverviewTabProps> = ({ policies }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [jurisdictionFilter, setJurisdictionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch statistics using React Query
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['overview-stats'],
    queryFn: fetchOverviewStats,
    staleTime: 60 * 1000
  });

  const stats = statsResponse?.data || {
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    scheduledUsers: 0,
    purgedUsers: 0,
    pendingRequests: 0,
    avgResponseTime: 0
  };

  // Fetch users using React Query with filters
  const { data: usersResponse, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['overview-users', searchQuery, jurisdictionFilter, statusFilter],
    queryFn: () => fetchOverviewUsers({
      search: searchQuery || undefined,
      jurisdiction: jurisdictionFilter !== 'all' ? jurisdictionFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined
    }),
    staleTime: 30 * 1000
  });

  const filteredUsers = usersResponse?.data || [];

  // Show loading state
  if (isLoadingStats || isLoadingUsers) {
    return <StandardLoadingState message="Loading overview data..." />;
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Developer Indicator */}
      {process.env.NODE_ENV === 'development' && filteredUsers.length > 0 && usersResponse?._usingMockData && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          background: '#E3F2FD',
          borderRadius: '4px',
          fontSize: '0.875rem',
          color: '#1565C0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span>ℹ️</span>
          <span>Showing mock data. Connect backend to persist changes.</span>
        </div>
      )}

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          padding: '1.5rem',
          background: 'var(--background-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Total Users Under Retention
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {stats.totalUsers}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
            {stats.totalUsers} users being tracked
          </div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'var(--background-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Users by Status
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {stats.activeUsers}/{stats.inactiveUsers}/{stats.scheduledUsers}/{stats.purgedUsers}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
            Active/Inactive/Scheduled/Purged
          </div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'rgba(255, 152, 0, 0.15)',
          borderRadius: '8px',
          border: '1px solid #FF9800'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#FF9800', marginBottom: '0.5rem' }}>
            Pending Deletion Requests
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#FF9800' }}>
            {stats.pendingRequests}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#FF9800', marginTop: '0.25rem' }}>
            Require attention (&lt;30 days)
          </div>
        </div>

        <div style={{
          padding: '1.5rem',
          background: 'var(--background-secondary)',
          borderRadius: '8px',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Average Response Time
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {stats.avgResponseTime} days
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
            Until scheduled purge
          </div>
        </div>
      </div>

      {/* Filters and User Table */}
      <div style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <input
            type="search"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              minWidth: '250px',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--background-primary)'
            }}
          />
          <select
            value={jurisdictionFilter}
            onChange={(e) => setJurisdictionFilter(e.target.value)}
            style={{
              width: '200px',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--background-primary)'
            }}
          >
            <option value="all">All Jurisdictions</option>
            <option value="US">US (CCPA)</option>
            <option value="Canada">Canada (PIPEDA)</option>
            <option value="UK">UK (GDPR)</option>
            <option value="Quebec">Quebec (Law 25)</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              width: '150px',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--background-primary)'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div style={{
          fontSize: '0.875rem',
          color: 'var(--text-primary)',
          marginBottom: '1rem'
        }}>
          Showing {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
        </div>

        {/* User Table */}
        <div style={{
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          overflow: 'auto'
        }}>
          <table style={{ width: '100%', minWidth: '1000px', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'var(--background-secondary)' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>User Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Jurisdiction</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Last Activity</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Days Until Purge</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr
                  key={user.id}
                  style={{
                    background: index % 2 === 0 ? 'var(--background-primary)' : 'var(--background-secondary)',
                    borderTop: '1px solid var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                >
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>{user.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>ID: {user.id}</div>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-primary)' }}>{user.email}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      background: user.jurisdiction.includes('US') ? 'rgba(33, 150, 243, 0.15)' :
                                 user.jurisdiction.includes('Canada') ? 'rgba(244, 67, 54, 0.15)' :
                                 user.jurisdiction.includes('UK') ? 'rgba(76, 175, 80, 0.15)' :
                                 'rgba(255, 152, 0, 0.15)',
                      color: user.jurisdiction.includes('US') ? '#2196F3' :
                             user.jurisdiction.includes('Canada') ? '#F44336' :
                             user.jurisdiction.includes('UK') ? '#4CAF50' :
                             '#FF9800',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      {user.jurisdiction}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-primary)' }}>{user.lastActivity}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      background: user.status === 'Active' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(255, 152, 0, 0.15)',
                      color: user.status === 'Active' ? '#4CAF50' : '#FF9800',
                      border: `1px solid ${user.status === 'Active' ? '#4CAF50' : '#FF9800'}`,
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      {user.status}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#4CAF50', fontWeight: 600 }}>
                    {user.daysUntilPurge} days
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        title="View details"
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'transparent',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        title="Download data"
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'transparent',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        title="Export data"
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'transparent',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <FileDown className="h-4 w-4" />
                      </button>
                      <button
                        title="Delete user"
                        style={{
                          padding: '0.25rem 0.5rem',
                          background: 'transparent',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          color: '#F44336',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================
// RETENTION POLICIES TAB
// ============================================

interface RetentionPoliciesTabProps {
  policies: RetentionPolicy[];
  onUpdatePolicy: (policyId: string, updates: Partial<RetentionPolicy>) => void;
  isUpdating: boolean;
}

const RetentionPoliciesTab: React.FC<RetentionPoliciesTabProps> = ({
  policies,
  onUpdatePolicy,
  isUpdating
}) => {
  const [editingPolicy, setEditingPolicy] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<RetentionPolicy>>({});

  const handleEdit = (policy: RetentionPolicy) => {
    setEditingPolicy(policy.id);
    setEditValues({
      inactivityPeriodDays: policy.inactivityPeriodDays,
      gracePeriodDays: policy.gracePeriodDays,
      autoDelete: policy.autoDelete,
      requiresApproval: policy.requiresApproval,
      notifyUserBeforeDays: policy.notifyUserBeforeDays,
      enabled: policy.enabled
    });
  };

  const handleSave = (policyId: string) => {
    onUpdatePolicy(policyId, editValues);
    setEditingPolicy(null);
    setEditValues({});
  };

  const handleCancel = () => {
    setEditingPolicy(null);
    setEditValues({});
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
          Retention Policies
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Configure data retention policies for different jurisdictions. These policies determine how long user data is retained after inactivity.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {policies.map(policy => (
          <div
            key={policy.id}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: 'var(--background-primary)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                  {policy.jurisdiction}
                </h3>
                <span
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: policy.enabled ? 'var(--success-bg)' : 'var(--error-bg)',
                    color: policy.enabled ? 'var(--success-text)' : 'var(--error-text)'
                  }}
                >
                  {policy.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {editingPolicy === policy.id ? (
                  <>
                    <button
                      onClick={() => handleSave(policy.id)}
                      disabled={isUpdating}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {isUpdating ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isUpdating}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'transparent',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleEdit(policy)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: 'var(--primary-color)',
                      border: '1px solid var(--primary-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Inactivity Period (Days)
                </label>
                {editingPolicy === policy.id ? (
                  <input
                    type="number"
                    value={editValues.inactivityPeriodDays ?? policy.inactivityPeriodDays}
                    onChange={(e) => setEditValues({ ...editValues, inactivityPeriodDays: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--background-primary)'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {policy.inactivityPeriodDays} days ({Math.floor(policy.inactivityPeriodDays / 365)} years)
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Grace Period (Days)
                </label>
                {editingPolicy === policy.id ? (
                  <input
                    type="number"
                    value={editValues.gracePeriodDays ?? policy.gracePeriodDays}
                    onChange={(e) => setEditValues({ ...editValues, gracePeriodDays: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--background-primary)'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {policy.gracePeriodDays} days
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Notify User Before (Days)
                </label>
                {editingPolicy === policy.id ? (
                  <input
                    type="number"
                    value={editValues.notifyUserBeforeDays ?? policy.notifyUserBeforeDays}
                    onChange={(e) => setEditValues({ ...editValues, notifyUserBeforeDays: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--background-primary)'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {policy.notifyUserBeforeDays} days
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Policy Status
                </label>
                {editingPolicy === policy.id ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editValues.enabled ?? policy.enabled}
                      onChange={(e) => setEditValues({ ...editValues, enabled: e.target.checked })}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                      Policy Enabled
                    </span>
                  </label>
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {policy.enabled ? 'Active' : 'Inactive'}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Auto Delete
                </label>
                {editingPolicy === policy.id ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editValues.autoDelete ?? policy.autoDelete}
                      onChange={(e) => setEditValues({ ...editValues, autoDelete: e.target.checked })}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                      Automatically delete data
                    </span>
                  </label>
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {policy.autoDelete ? 'Yes' : 'No'}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Requires Approval
                </label>
                {editingPolicy === policy.id ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editValues.requiresApproval ?? policy.requiresApproval}
                      onChange={(e) => setEditValues({ ...editValues, requiresApproval: e.target.checked })}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                      Require admin approval
                    </span>
                  </label>
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {policy.requiresApproval ? 'Yes' : 'No'}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// SLA SETTINGS TAB
// ============================================

interface SLASettingsTabProps {
  slaSettings: SLASetting[];
  onUpdateSLA: (slaId: string, updates: Partial<SLASetting>) => void;
  isUpdating: boolean;
}

const SLASettingsTab: React.FC<SLASettingsTabProps> = ({
  slaSettings,
  onUpdateSLA,
  isUpdating
}) => {
  const [editingSLA, setEditingSLA] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<SLASetting>>({});

  const handleEdit = (sla: SLASetting) => {
    setEditingSLA(sla.id);
    setEditValues({
      responseDays: sla.responseDays,
      completionDays: sla.completionDays,
      escalationDays: sla.escalationDays,
      enabled: sla.enabled
    });
  };

  const handleSave = (slaId: string) => {
    onUpdateSLA(slaId, editValues);
    setEditingSLA(null);
    setEditValues({});
  };

  const handleCancel = () => {
    setEditingSLA(null);
    setEditValues({});
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
          SLA Settings
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Configure Service Level Agreement (SLA) timelines for different types of data deletion requests.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {slaSettings.map(sla => (
          <div
            key={sla.id}
            style={{
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: 'var(--background-primary)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                  {sla.requestTypeLabel}
                </h3>
                <span
                  style={{
                    fontSize: '12px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: sla.enabled ? 'var(--success-bg)' : 'var(--error-bg)',
                    color: sla.enabled ? 'var(--success-text)' : 'var(--error-text)'
                  }}
                >
                  {sla.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {editingSLA === sla.id ? (
                  <>
                    <button
                      onClick={() => handleSave(sla.id)}
                      disabled={isUpdating}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {isUpdating ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isUpdating}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'transparent',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleEdit(sla)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: 'var(--primary-color)',
                      border: '1px solid var(--primary-color)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Response Days
                </label>
                {editingSLA === sla.id ? (
                  <input
                    type="number"
                    value={editValues.responseDays ?? sla.responseDays}
                    onChange={(e) => setEditValues({ ...editValues, responseDays: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--background-primary)'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {sla.responseDays} days
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Completion Days
                </label>
                {editingSLA === sla.id ? (
                  <input
                    type="number"
                    value={editValues.completionDays ?? sla.completionDays}
                    onChange={(e) => setEditValues({ ...editValues, completionDays: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--background-primary)'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {sla.completionDays} days
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Escalation Days
                </label>
                {editingSLA === sla.id ? (
                  <input
                    type="number"
                    value={editValues.escalationDays ?? sla.escalationDays}
                    onChange={(e) => setEditValues({ ...editValues, escalationDays: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--background-primary)'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {sla.escalationDays} days
                  </div>
                )}
              </div>
            </div>

            {editingSLA === sla.id && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editValues.enabled ?? sla.enabled}
                    onChange={(e) => setEditValues({ ...editValues, enabled: e.target.checked })}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    SLA Enabled
                  </span>
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// AUTOMATION SETTINGS TAB
// ============================================

interface AutomationSettingsTabProps {
  automation: AutomationSettings;
  onUpdate: (updates: Partial<AutomationSettings>) => void;
  isUpdating: boolean;
}

const AutomationSettingsTab: React.FC<AutomationSettingsTabProps> = ({
  automation,
  onUpdate,
  isUpdating
}) => {
  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
          Automation Settings
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Configure automated processes for data retention and deletion.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Auto Deletion Settings */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Auto Deletion
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={automation.autoDeletionEnabled}
                onChange={(e) => onUpdate({ autoDeletionEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Enable automatic deletion
              </span>
            </label>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Deletion Schedule
                </label>
                <select
                  value={automation.autoDeletionSchedule}
                  onChange={(e) => onUpdate({ autoDeletionSchedule: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                  disabled={!automation.autoDeletionEnabled || isUpdating}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--background-primary)',
                    cursor: !automation.autoDeletionEnabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Deletion Time
                </label>
                <input
                  type="time"
                  value={automation.autoDeletionTime}
                  onChange={(e) => onUpdate({ autoDeletionTime: e.target.value })}
                  disabled={!automation.autoDeletionEnabled || isUpdating}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--background-primary)',
                    cursor: !automation.autoDeletionEnabled ? 'not-allowed' : 'pointer'
                  }}
                />
              </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={automation.retryFailedDeletions}
                onChange={(e) => onUpdate({ retryFailedDeletions: e.target.checked })}
                disabled={!automation.autoDeletionEnabled || isUpdating}
                style={{ width: '16px', height: '16px', cursor: automation.autoDeletionEnabled ? 'pointer' : 'not-allowed' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Retry failed deletions
              </span>
            </label>

            {automation.retryFailedDeletions && (
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Max Retry Attempts
                </label>
                <input
                  type="number"
                  value={automation.maxRetryAttempts}
                  onChange={(e) => onUpdate({ maxRetryAttempts: parseInt(e.target.value) })}
                  disabled={!automation.autoDeletionEnabled || isUpdating}
                  min="1"
                  max="10"
                  style={{
                    width: '200px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--background-primary)'
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Auto Reminder Settings */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Auto Reminders
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={automation.autoReminderEnabled}
                onChange={(e) => onUpdate({ autoReminderEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Enable automatic reminders
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Send Reminder (Days Before)
              </label>
              <input
                type="number"
                value={automation.autoReminderDaysBefore}
                onChange={(e) => onUpdate({ autoReminderDaysBefore: parseInt(e.target.value) })}
                disabled={!automation.autoReminderEnabled || isUpdating}
                min="1"
                max="90"
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !automation.autoReminderEnabled ? 'not-allowed' : 'pointer'
                }}
              />
            </div>
          </div>
        </div>

        {/* Batch Processing */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Batch Processing
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={automation.batchProcessingEnabled}
                onChange={(e) => onUpdate({ batchProcessingEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Enable batch processing
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Batch Size
              </label>
              <input
                type="number"
                value={automation.batchSize}
                onChange={(e) => onUpdate({ batchSize: parseInt(e.target.value) })}
                disabled={!automation.batchProcessingEnabled || isUpdating}
                min="10"
                max="1000"
                step="10"
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !automation.batchProcessingEnabled ? 'not-allowed' : 'pointer'
                }}
              />
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Notifications
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={automation.autoNotifyUsers}
                onChange={(e) => onUpdate({ autoNotifyUsers: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Automatically notify users
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={automation.autoNotifyAdmins}
                onChange={(e) => onUpdate({ autoNotifyAdmins: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Automatically notify administrators
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// NOTIFICATIONS SETTINGS TAB
// ============================================

interface NotificationsSettingsTabProps {
  notifications: NotificationSettings;
  onUpdate: (updates: Partial<NotificationSettings>) => void;
  isUpdating: boolean;
}

const NotificationsSettingsTab: React.FC<NotificationsSettingsTabProps> = ({
  notifications,
  onUpdate,
  isUpdating
}) => {
  const [escalationRecipients, setEscalationRecipients] = useState(notifications.escalationRecipients.join(', '));

  const handleEscalationRecipientsChange = () => {
    const recipients = escalationRecipients.split(',').map(email => email.trim()).filter(email => email);
    onUpdate({ escalationRecipients: recipients });
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
          Notification Settings
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Configure notification preferences for administrators and users.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Admin Notifications */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Admin Notifications
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.adminNotifications.newRequests}
                onChange={(e) => onUpdate({
                  adminNotifications: { ...notifications.adminNotifications, newRequests: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Notify on new deletion requests
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.adminNotifications.overdueRequests}
                onChange={(e) => onUpdate({
                  adminNotifications: { ...notifications.adminNotifications, overdueRequests: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Notify on overdue requests
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.adminNotifications.completedRequests}
                onChange={(e) => onUpdate({
                  adminNotifications: { ...notifications.adminNotifications, completedRequests: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Notify on completed requests
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.adminNotifications.failedDeletions}
                onChange={(e) => onUpdate({
                  adminNotifications: { ...notifications.adminNotifications, failedDeletions: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Notify on failed deletions
              </span>
            </label>
          </div>
        </div>

        {/* User Notifications */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            User Notifications
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.userNotifications.deletionWarning}
                onChange={(e) => onUpdate({
                  userNotifications: { ...notifications.userNotifications, deletionWarning: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Send deletion warning
              </span>
            </label>

            {notifications.userNotifications.deletionWarning && (
              <div style={{ marginLeft: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Warning Days Before
                </label>
                <input
                  type="number"
                  value={notifications.userNotifications.deletionWarningDays}
                  onChange={(e) => onUpdate({
                    userNotifications: { ...notifications.userNotifications, deletionWarningDays: parseInt(e.target.value) }
                  })}
                  disabled={isUpdating}
                  min="1"
                  max="90"
                  style={{
                    width: '200px',
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--background-primary)'
                  }}
                />
              </div>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.userNotifications.deletionConfirmation}
                onChange={(e) => onUpdate({
                  userNotifications: { ...notifications.userNotifications, deletionConfirmation: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Send deletion confirmation
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.userNotifications.deletionComplete}
                onChange={(e) => onUpdate({
                  userNotifications: { ...notifications.userNotifications, deletionComplete: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Send deletion completion notification
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.userNotifications.requestReceived}
                onChange={(e) => onUpdate({
                  userNotifications: { ...notifications.userNotifications, requestReceived: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Confirm request received
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.userNotifications.requestApproved}
                onChange={(e) => onUpdate({
                  userNotifications: { ...notifications.userNotifications, requestApproved: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Notify when request approved
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.userNotifications.requestDenied}
                onChange={(e) => onUpdate({
                  userNotifications: { ...notifications.userNotifications, requestDenied: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Notify when request denied
              </span>
            </label>
          </div>
        </div>

        {/* Notification Methods */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Notification Methods
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.notificationMethods.email}
                onChange={(e) => onUpdate({
                  notificationMethods: { ...notifications.notificationMethods, email: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Email notifications
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.notificationMethods.sms}
                onChange={(e) => onUpdate({
                  notificationMethods: { ...notifications.notificationMethods, sms: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                SMS notifications
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.notificationMethods.inApp}
                onChange={(e) => onUpdate({
                  notificationMethods: { ...notifications.notificationMethods, inApp: e.target.checked }
                })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                In-app notifications
              </span>
            </label>
          </div>
        </div>

        {/* Escalation Settings */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Escalation Settings
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notifications.escalationEnabled}
                onChange={(e) => onUpdate({ escalationEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Enable escalation
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Escalation Days
              </label>
              <input
                type="number"
                value={notifications.escalationDays}
                onChange={(e) => onUpdate({ escalationDays: parseInt(e.target.value) })}
                disabled={!notifications.escalationEnabled || isUpdating}
                min="1"
                max="30"
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !notifications.escalationEnabled ? 'not-allowed' : 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Escalation Recipients (comma-separated emails)
              </label>
              <input
                type="text"
                value={escalationRecipients}
                onChange={(e) => setEscalationRecipients(e.target.value)}
                onBlur={handleEscalationRecipientsChange}
                disabled={!notifications.escalationEnabled || isUpdating}
                placeholder="email1@company.com, email2@company.com"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !notifications.escalationEnabled ? 'not-allowed' : 'pointer'
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPLIANCE SETTINGS TAB
// ============================================

interface ComplianceSettingsTabProps {
  compliance: ComplianceSettings;
  onUpdate: (updates: Partial<ComplianceSettings>) => void;
  isUpdating: boolean;
}

const ComplianceSettingsTab: React.FC<ComplianceSettingsTabProps> = ({
  compliance,
  onUpdate,
  isUpdating
}) => {
  const [thirdPartyProcessors, setThirdPartyProcessors] = useState(compliance.thirdPartyProcessors.join(', '));
  const [verificationMethods, setVerificationMethods] = useState(compliance.verificationMethods.join(', '));
  const [dataExportFormats, setDataExportFormats] = useState(compliance.dataExportFormats.join(', '));

  const handleThirdPartyProcessorsChange = () => {
    const processors = thirdPartyProcessors.split(',').map(p => p.trim()).filter(p => p);
    onUpdate({ thirdPartyProcessors: processors });
  };

  const handleVerificationMethodsChange = () => {
    const methods = verificationMethods.split(',').map(m => m.trim()).filter(m => m);
    onUpdate({ verificationMethods: methods });
  };

  const handleDataExportFormatsChange = () => {
    const formats = dataExportFormats.split(',').map(f => f.trim()).filter(f => f);
    onUpdate({ dataExportFormats: formats });
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
          Compliance Settings
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Configure compliance and regulatory settings for data retention and deletion.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Audit Logging */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Audit Logging
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.auditLoggingEnabled}
                onChange={(e) => onUpdate({ auditLoggingEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Enable audit logging
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Audit Log Retention (Days)
              </label>
              <input
                type="number"
                value={compliance.auditLogRetentionDays}
                onChange={(e) => onUpdate({ auditLogRetentionDays: parseInt(e.target.value) })}
                disabled={!compliance.auditLoggingEnabled || isUpdating}
                min="365"
                max="3650"
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !compliance.auditLoggingEnabled ? 'not-allowed' : 'pointer'
                }}
              />
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {Math.floor(compliance.auditLogRetentionDays / 365)} years
              </p>
            </div>
          </div>
        </div>

        {/* Verification */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Verification
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.verificationRequired}
                onChange={(e) => onUpdate({ verificationRequired: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Require identity verification
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Verification Methods (comma-separated)
              </label>
              <input
                type="text"
                value={verificationMethods}
                onChange={(e) => setVerificationMethods(e.target.value)}
                onBlur={handleVerificationMethodsChange}
                disabled={!compliance.verificationRequired || isUpdating}
                placeholder="email, identity_document, 2fa"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !compliance.verificationRequired ? 'not-allowed' : 'pointer'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Data Export Formats (comma-separated)
              </label>
              <input
                type="text"
                value={dataExportFormats}
                onChange={(e) => setDataExportFormats(e.target.value)}
                onBlur={handleDataExportFormatsChange}
                disabled={isUpdating}
                placeholder="json, csv, pdf"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)'
                }}
              />
            </div>
          </div>
        </div>

        {/* Legal Hold */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Legal Hold
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.legalHoldEnabled}
                onChange={(e) => onUpdate({ legalHoldEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Enable legal hold
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.legalHoldBypassApproval}
                onChange={(e) => onUpdate({ legalHoldBypassApproval: e.target.checked })}
                disabled={!compliance.legalHoldEnabled || isUpdating}
                style={{ width: '16px', height: '16px', cursor: compliance.legalHoldEnabled ? 'pointer' : 'not-allowed' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Require approval to bypass legal hold
              </span>
            </label>
          </div>
        </div>

        {/* Third Party Processing */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Third Party Processing
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.thirdPartyNotificationEnabled}
                onChange={(e) => onUpdate({ thirdPartyNotificationEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Notify third party processors
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Third Party Processors (comma-separated)
              </label>
              <input
                type="text"
                value={thirdPartyProcessors}
                onChange={(e) => setThirdPartyProcessors(e.target.value)}
                onBlur={handleThirdPartyProcessorsChange}
                disabled={!compliance.thirdPartyNotificationEnabled || isUpdating}
                placeholder="AWS, SendGrid, Stripe"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !compliance.thirdPartyNotificationEnabled ? 'not-allowed' : 'pointer'
                }}
              />
            </div>
          </div>
        </div>

        {/* Compliance Reporting */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Compliance Reporting
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.complianceReportingEnabled}
                onChange={(e) => onUpdate({ complianceReportingEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 500 }}>
                Enable compliance reporting
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '8px' }}>
                Reporting Frequency
              </label>
              <select
                value={compliance.complianceReportingFrequency}
                onChange={(e) => onUpdate({ complianceReportingFrequency: e.target.value as 'weekly' | 'monthly' | 'quarterly' })}
                disabled={!compliance.complianceReportingEnabled || isUpdating}
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--background-primary)',
                  cursor: !compliance.complianceReportingEnabled ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
          </div>
        </div>

        {/* Data Minimization */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '16px' }}>
            Data Minimization
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.dataMinimizationEnabled}
                onChange={(e) => onUpdate({ dataMinimizationEnabled: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Enable data minimization
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={compliance.anonymizationBeforeDeletion}
                onChange={(e) => onUpdate({ anonymizationBeforeDeletion: e.target.checked })}
                disabled={isUpdating}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Anonymize data before deletion
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const DataRetentionManager: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<RetentionTab>('overview');

  // Fetch retention settings (policies and SLA)
  const { data: retentionSettingsResponse, isLoading: isLoadingSettings, error: settingsError } = useQuery({
    queryKey: ['retentionSettings'],
    queryFn: fetchRetentionSettings,
    staleTime: 60 * 1000
  });

  // Fetch advanced settings
  const { data: advancedSettingsResponse, isLoading: isLoadingAdvanced, error: advancedError } = useQuery({
    queryKey: ['advancedRetentionSettings'],
    queryFn: fetchAdvancedRetentionSettings,
    staleTime: 60 * 1000
  });

  // Mutation for updating retention policy
  const updatePolicyMutation = useMutation({
    mutationFn: ({ policyId, updates }: { policyId: string; updates: Partial<RetentionPolicy> }) =>
      updateRetentionPolicy(policyId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retentionSettings'] });
    }
  });

  // Mutation for updating SLA settings
  const updateSLAMutation = useMutation({
    mutationFn: ({ slaId, updates }: { slaId: string; updates: Partial<SLASetting> }) =>
      updateSLASettings(slaId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['retentionSettings'] });
    }
  });

  // Mutation for updating automation settings
  const updateAutomationMutation = useMutation({
    mutationFn: updateAutomationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advancedRetentionSettings'] });
    }
  });

  // Mutation for updating notification settings
  const updateNotificationsMutation = useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advancedRetentionSettings'] });
    }
  });

  // Mutation for updating compliance settings
  const updateComplianceMutation = useMutation({
    mutationFn: updateComplianceSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advancedRetentionSettings'] });
    }
  });

  // Handlers
  const handleUpdatePolicy = (policyId: string, updates: Partial<RetentionPolicy>) => {
    updatePolicyMutation.mutate({ policyId, updates });
  };

  const handleUpdateSLA = (slaId: string, updates: Partial<SLASetting>) => {
    updateSLAMutation.mutate({ slaId, updates });
  };

  const handleUpdateAutomation = (updates: Partial<AutomationSettings>) => {
    updateAutomationMutation.mutate(updates);
  };

  const handleUpdateNotifications = (updates: Partial<NotificationSettings>) => {
    updateNotificationsMutation.mutate(updates);
  };

  const handleUpdateCompliance = (updates: Partial<ComplianceSettings>) => {
    updateComplianceMutation.mutate(updates);
  };

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'policies', label: 'Retention Policies', icon: '📋' },
    { id: 'sla', label: 'SLA Settings', icon: '⏱️' },
    { id: 'automation', label: 'Automation', icon: '🤖' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'compliance', label: 'Compliance', icon: '⚖️' }
  ];

  const isLoading = isLoadingSettings || isLoadingAdvanced;
  const error = settingsError || advancedError;

  if (isLoading) {
    return <StandardLoadingState message="Loading data retention settings..." />;
  }

  if (error) {
    return (
      <StandardErrorState
        title="Error loading settings"
        message={error instanceof Error ? error.message : 'Unknown error'}
      />
    );
  }

  const retentionSettings = retentionSettingsResponse?.data;
  const advancedSettings = advancedSettingsResponse?.data;

  return (
    <div className="data-retention-manager" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div
        style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--background-primary)'
        }}
      >
        <h1 style={{ fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
          Data Retention & Deletion Management
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
          Configure data retention policies, SLA settings, and compliance requirements
        </p>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          padding: '16px 24px',
          borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--background-primary)',
          overflowX: 'auto'
        }}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? 700 : 600,
              color: 'var(--text-primary)',
              background: activeTab === tab.id ? 'var(--background-primary)' : 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid var(--primary-color)' : '2px solid transparent',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            <span style={{ marginRight: '8px' }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, overflow: 'auto', backgroundColor: 'var(--background-secondary)' }}>
        {activeTab === 'overview' && retentionSettings && (
          <OverviewTab
            policies={retentionSettings.retentionPolicies}
          />
        )}

        {activeTab === 'policies' && retentionSettings && (
          <RetentionPoliciesTab
            policies={retentionSettings.retentionPolicies}
            onUpdatePolicy={handleUpdatePolicy}
            isUpdating={updatePolicyMutation.isPending}
          />
        )}

        {activeTab === 'sla' && retentionSettings && (
          <SLASettingsTab
            slaSettings={retentionSettings.slaSettings}
            onUpdateSLA={handleUpdateSLA}
            isUpdating={updateSLAMutation.isPending}
          />
        )}

        {activeTab === 'automation' && advancedSettings && (
          <AutomationSettingsTab
            automation={advancedSettings.automation}
            onUpdate={handleUpdateAutomation}
            isUpdating={updateAutomationMutation.isPending}
          />
        )}

        {activeTab === 'notifications' && advancedSettings && (
          <NotificationsSettingsTab
            notifications={advancedSettings.notifications}
            onUpdate={handleUpdateNotifications}
            isUpdating={updateNotificationsMutation.isPending}
          />
        )}

        {activeTab === 'compliance' && advancedSettings && (
          <ComplianceSettingsTab
            compliance={advancedSettings.compliance}
            onUpdate={handleUpdateCompliance}
            isUpdating={updateComplianceMutation.isPending}
          />
        )}
      </div>
    </div>
  );
};

export default DataRetentionManager;
